import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDuration(seconds: number): string {
  if (seconds < 0) return 'N/A'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`
  } else {
    return `${secs}s`
  }
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount)
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const STEM_COLORS: Record<string, string> = {
  vocals: 'bg-pink-500',
  bass: 'bg-orange-500',
  drums: 'bg-yellow-500',
  guitar: 'bg-green-500',
  piano: 'bg-blue-500',
  other: 'bg-purple-500',
  instrumental: 'bg-indigo-500'
}

export interface ModelInfoType {
  stems: number
  description: string
  framework: 'demucs' | 'audio_separator' | 'ensemble'
  sdr?: number
  bestFor?: string
  memoryGb?: number
}

export const MODEL_INFO: Record<string, ModelInfoType> = {
  // Demucs models
  htdemucs: {
    stems: 4,
    description: 'Hybrid Transformer Demucs (4 stems)',
    framework: 'demucs',
    sdr: 8.5,
    bestFor: 'General purpose',
    memoryGb: 8
  },
  htdemucs_ft: {
    stems: 4,
    description: 'Fine-tuned HTDemucs (4 stems)',
    framework: 'demucs',
    sdr: 9.0,
    bestFor: 'High quality vocals',
    memoryGb: 8
  },
  htdemucs_6s: {
    stems: 6,
    description: '6-Stem Separation',
    framework: 'demucs',
    sdr: 8.8,
    bestFor: 'Instrument separation',
    memoryGb: 10
  },
  mdx23c: {
    stems: 2,
    description: 'MDX23C (vocals/instrumental)',
    framework: 'audio_separator',
    sdr: 9.2,
    bestFor: 'Fast vocal extraction',
    memoryGb: 6
  },
  // Audio-separator models (RoFormer family)
  mel_band_roformer: {
    stems: 2,
    description: 'Mel-Band RoFormer (vocals/instrumental)',
    framework: 'audio_separator',
    sdr: 9.8,
    bestFor: 'Best vocal extraction',
    memoryGb: 6
  },
  bs_roformer: {
    stems: 2,
    description: 'BS-RoFormer (vocals/instrumental)',
    framework: 'audio_separator',
    sdr: 9.8,
    bestFor: 'Best vocal extraction',
    memoryGb: 8
  },
  // Ensemble mode
  ensemble: {
    stems: 4,
    description: 'Ensemble (multiple models)',
    framework: 'ensemble',
    sdr: 10.0,
    bestFor: 'Maximum quality',
    memoryGb: 16
  }
}

export const MERGE_STRATEGIES = [
  { id: 'snr_weighted', name: 'SNR Weighted', description: 'Weight by signal-to-noise ratio (recommended)' },
  { id: 'average', name: 'Average', description: 'Simple average of all models' },
  { id: 'max_energy', name: 'Max Energy', description: 'Select highest energy version per stem' }
]
